import { EntryType, RawQuestionnaireEntry } from '../../../hooks/import/models'

// Projects the questionnaire DOCX parser's output (`RawQuestionnaireEntry[]`)
// into a position-keyed map of plain-text translations. The keys it emits
// (`Q1.text`, `Q2.option.3`, `Q4.row.2`, `Q4.col.1`, `A1.text`, …) are exactly
// the `field.key` shapes [extractTranslatableFields.ts] and
// [extractAudienceFields.ts] produce, so the caller can look up live target
// fields by direct string equality with no additional parsing.
//
// The function is intentionally pure (no hooks, no live-survey awareness) so
// it's trivially unit-testable. Whether a particular key matches a live field
// — and the "extra positions in the file" diagnostic — is computed by the
// caller after intersecting the returned map with the live field set.
export const projectDocxToTranslations = (
  rawEntries: ReadonlyArray<RawQuestionnaireEntry>
): Map<string, string> => {
  const texts = new Map<string, string>()

  const set = (key: string, value: string | undefined | null) => {
    if (!value) return
    const trimmed = value.trim()
    if (!trimmed) return
    texts.set(key, trimmed)
  }

  for (const entry of rawEntries) {
    // `qNum` is whatever `getQuestionNumber()` pulled out of the title via
    // /[AQT]\d+/. Entries with no recognisable prefix have none and can't be
    // addressed positionally — skip them.
    if (!entry.qNum) continue
    const prefix = entry.qNum // e.g. "Q1", "A2"

    if (entry.entryType === EntryType.TextCardType) {
      // TextCard maps title/subtitle/body 1:1 with the live field keys.
      set(`${prefix}.title`, entry.title)
      set(`${prefix}.subtitle`, entry.subtitle)
      set(`${prefix}.body`, entry.body)
      continue
    }

    if (entry.entryType === EntryType.TextHighlighterType) {
      // The parser already carries everything a highlighter needs — the
      // passage in `body`, the categories in `options` — but the live keys
      // differ from the basic-question ones (`.passage` / `.category.<n>`,
      // not `.body` / `.option.<n>`), so it needs its own branch. Without it
      // the passage silently never imported and every category came back as
      // an "extra position in file".
      set(`${prefix}.text`, entry.title)
      set(`${prefix}.passage`, entry.body)
      entry.options.forEach((category, idx) => {
        set(`${prefix}.category.${idx + 1}`, category.value)
      })
      continue
    }

    if (entry.entryType === EntryType.MatrixType) {
      // Matrix: parser puts rows in `.rows` and columns in `.options` (split
      // at the literal "Columns" paragraph in the source doc). Live extract
      // matches: matrix rows are Q<n>.row.<m>; matrix columns are
      // Q<n>.col.<m> (still `kind: 'responseOptionValue'` under the hood,
      // shared dispatch with non-matrix options).
      set(`${prefix}.title`, entry.title)
      entry.rows.forEach((row, idx) => {
        set(`${prefix}.row.${idx + 1}`, row.value)
      })
      entry.options.forEach((col, idx) => {
        set(`${prefix}.col.${idx + 1}`, col.value)
      })
      continue
    }

    if (entry.entryType === EntryType.ScaleType) {
      // Slider endpoint labels are keyed by scale position, not by ordinal —
      // a label is only written when it has text, so an unlabelled centre
      // would otherwise shift the ones after it.
      set(`${prefix}.text`, entry.title)
      entry.scaleLabels?.forEach((label) => {
        set(`${prefix}.scaleLabel.${label.position}`, label.value)
      })
      continue
    }

    if (entry.entryType === EntryType.MaxDiffType) {
      set(`${prefix}.text`, entry.title)
      entry.options.forEach((opt, idx) => {
        set(`${prefix}.option.${idx + 1}`, opt.value)
      })
      set(`${prefix}.mostLabel`, entry.maxDiffLabels?.mostLabel)
      set(`${prefix}.leastLabel`, entry.maxDiffLabels?.leastLabel)
      set(`${prefix}.attributeLabel`, entry.maxDiffLabels?.attributeLabel)
      continue
    }

    if (entry.entryType === EntryType.ConjointType) {
      set(`${prefix}.text`, entry.title)
      entry.attributes?.forEach((attribute, attrIdx) => {
        set(`${prefix}.attribute.${attrIdx + 1}`, attribute.name)
        attribute.levels.forEach((level, levelIdx) => {
          set(`${prefix}.attribute.${attrIdx + 1}.level.${levelIdx + 1}`, level)
        })
      })
      continue
    }

    // Basic-like: BasicType / RankedType / FreeTextType — title + options[].
    // Audience custom screeners come through the same parser path with an
    // `A`-prefixed qNum, and their live keys are A<n>.text / A<n>.option.<m>
    // (no rows/cols), so this branch covers them too without a separate
    // audience check.
    set(`${prefix}.text`, entry.title)
    entry.options.forEach((opt, idx) => {
      set(`${prefix}.option.${idx + 1}`, opt.value)
    })
  }

  return texts
}
